import React, { useEffect, useState } from "react";

interface AnimatedLogoLoaderProps {
  isLoading?: boolean; // Control visibility with loading state
  minDisplayTime?: number; // Minimum time to display in ms
  onComplete?: () => void; // Callback when animation completes
}

// Logo shape path from the original logo component
const logoPath = "M200 400L0 200L100 100L200 200L300 100L400 200L300 300L200 400Z";
const innerDiamondPath = "M200 250L250 200L200 150L150 200L200 250Z";

const AnimatedLogoLoader: React.FC<AnimatedLogoLoaderProps> = ({
  isLoading = true,
  minDisplayTime = 800, // Default minimum display time
  onComplete,
}) => {
  const [isVisible, setIsVisible] = useState(isLoading);
  const [isFading, setIsFading] = useState(false);

  useEffect(() => {
    // When loading state changes to true, show the loader
    if (isLoading) {
      setIsVisible(true);
      setIsFading(false);
    }
    
    // When loading state changes to false, start fade out process
    if (!isLoading && isVisible) {
      // Start the timer for minimum display time
      const timer = setTimeout(() => {
        setIsFading(true);
        
        // After fade out animation completes, hide the element completely
        const fadeOutTimer = setTimeout(() => {
          setIsVisible(false);
          if (onComplete) onComplete();
        }, 500); // Duration of the fade animation
        
        return () => clearTimeout(fadeOutTimer);
      }, minDisplayTime);
      
      return () => clearTimeout(timer);
    }
  }, [isLoading, isVisible, minDisplayTime, onComplete]);

  if (!isVisible) return null;

  return (
    <div 
      className={`fixed inset-0 z-50 flex items-center justify-center bg-black transition-opacity duration-500 ${isFading ? 'opacity-0' : 'opacity-100'}`}
    >
      <div className="relative w-32 h-32">
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          viewBox="0 0 400 400" 
          className="w-full h-full"
          style={{ filter: "drop-shadow(0 0 10px rgba(156, 81, 244, 0.5))" }}
        >
          <defs>
            {/* Gradient for the main logo */}
            <linearGradient id="animatedLogoGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#9c51f4">
                <animate 
                  attributeName="offset" 
                  values="0;0.1;0" 
                  dur="2s" 
                  repeatCount="indefinite" 
                />
              </stop>
              <stop offset="20%" stopColor="#a855f7">
                <animate 
                  attributeName="offset" 
                  values="0.2;0.3;0.2" 
                  dur="2s" 
                  repeatCount="indefinite" 
                />
              </stop>
              <stop offset="40%" stopColor="#d946ef">
                <animate 
                  attributeName="offset" 
                  values="0.4;0.5;0.4" 
                  dur="2s" 
                  repeatCount="indefinite" 
                />
              </stop>
              <stop offset="60%" stopColor="#e11d48">
                <animate 
                  attributeName="offset" 
                  values="0.6;0.7;0.6" 
                  dur="2s" 
                  repeatCount="indefinite" 
                />
              </stop>
              <stop offset="80%" stopColor="#dc2626">
                <animate 
                  attributeName="offset" 
                  values="0.8;0.9;0.8" 
                  dur="2s" 
                  repeatCount="indefinite" 
                />
              </stop>
              <stop offset="100%" stopColor="#b91c1c">
                <animate 
                  attributeName="offset" 
                  values="1;1.1;1" 
                  dur="2s" 
                  repeatCount="indefinite" 
                />
              </stop>
            </linearGradient>
            
            {/* Animation for rotation */}
            <animateTransform
              id="rotate"
              attributeName="transform"
              attributeType="XML"
              type="rotate"
              from="0 200 200"
              to="360 200 200"
              dur="3s"
              repeatCount="indefinite"
            />
          </defs>
          
          {/* Main logo shape with animation */}
          <g>
            {/* Outer logo shape */}
            <path 
              fill="url(#animatedLogoGradient)" 
              d={logoPath}
              className="animate-pulse"
            >
              {/* Subtle scale animation */}
              <animate 
                attributeName="d" 
                values={`${logoPath};M200 410L-10 200L100 90L200 190L300 90L410 200L300 310L200 410Z;${logoPath}`} 
                dur="2s" 
                repeatCount="indefinite" 
              />
            </path>
            
            {/* Inner diamond */}
            <path 
              fill="#ffffff" 
              d={innerDiamondPath}
              opacity="0.9"
            >
              {/* Pulse animation for inner diamond */}
              <animate 
                attributeName="opacity" 
                values="0.7;1;0.7" 
                dur="1.5s" 
                repeatCount="indefinite" 
              />
            </path>
          </g>
        </svg>
      </div>
    </div>
  );
};

export default AnimatedLogoLoader; 